import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  PiChatCircleText, PiX, PiArrowsOut, PiArrowsIn, PiNotePencil, PiArrowUp, PiStop,
  PiArrowDown, PiWarningCircle, PiThermometerSimple, PiDrop, PiPlant, PiLightning, PiHash,
} from 'react-icons/pi';
import { Link } from 'react-router-dom';
import { issuesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Lightbox } from './RackPhotoGallery';
import useChat from './chat/useChat';
import AssistantTurn from './chat/AssistantTurn';
import { plainText } from './chat/Markdown';

/* The farm assistant: a panel on every signed-in page that answers questions from the
 * farm's own records. The server runs an agent that calls read-only farm tools and
 * streams NDJSON back (see chatAPI.streamMessage); this file is the shell around it.
 *
 * Desktop: a 440 px panel at the bottom right, widened to a reading column on demand
 * (remembered). Phones: a sheet from the top of the screen down to the bottom bar, so
 * the bar stays usable and nothing hides behind it. The conversation lives in
 * sessionStorage (useChat), so it follows page changes and survives a reload. */

const OPEN_KEY = 'nk.chat.open';
/* Any page can open the bubble with an issue tagged:
   window.dispatchEvent(new CustomEvent(TAG_ISSUE_EVENT, { detail: { id, title } })) */
export const TAG_ISSUE_EVENT = 'nk:chat-tag-issue';
const WIDE_KEY = 'nk.chat.wide';

// Height of Layout's phone bottom bar: 6 px padding + 48 px tab + 1 px border, plus
// max(6 px, safe area) underneath. The sheet stops exactly on top of it.
const BOTTOM_BAR = 'calc(55px + max(6px, env(safe-area-inset-bottom)))';

const STARTERS = [
  { icon: PiWarningCircle, text: 'What needs attention today?' },
  { icon: PiThermometerSimple, text: "How did the container's temperature do today?" },
  { icon: PiDrop, text: "Which tank's pH is drifting?" },
  { icon: PiPlant, text: "How are Rack B's plants doing?" },
  { icon: PiLightning, text: 'Power use this week vs last week' },
];

const readFlag = (store, key) => { try { return store.getItem(key) === '1'; } catch (e) { return false; } };
const writeFlag = (store, key, on) => { try { store.setItem(key, on ? '1' : '0'); } catch (e) { /* blocked */ } };

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.985); }
  to { opacity: 1; transform: none; }
`;

/* ------------------------------------------------------------------ launcher */

const Launcher = styled.button`
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 900;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 18px 0 15px;
  border: 0;
  border-radius: 999px;
  background: ${p => p.theme.colors.text};
  color: ${p => p.theme.colors.surface};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  box-shadow: ${p => p.theme.shadows.lg};
  cursor: pointer;
  transition: transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1);
  svg { font-size: 20px; }
  &:hover { transform: translateY(-1px); }
  &:active { transform: scale(0.97); }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 3px; }

  @media (max-width: ${p => p.theme.breakpoints.md}) {
    right: 14px;
    bottom: calc(${BOTTOM_BAR} + 12px);
    width: 52px;
    height: 52px;
    padding: 0;
    justify-content: center;
    svg { font-size: 23px; }
    .label { display: none; }
  }
`;

/* ------------------------------------------------------------------ panel */

const Panel = styled.section`
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 990;
  width: ${p => (p.$wide ? 'min(800px, calc(100vw - 40px))' : '440px')};
  height: ${p => (p.$wide ? 'calc(100vh - 40px)' : 'min(720px, calc(100vh - 40px))')};
  height: ${p => (p.$wide ? 'calc(100dvh - 40px)' : 'min(720px, calc(100dvh - 40px))')};
  display: flex;
  flex-direction: column;
  background: ${p => p.theme.colors.surface};
  color: ${p => p.theme.colors.text};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 16px;
  box-shadow: ${p => p.theme.shadows.lg};
  overflow: hidden;
  transform-origin: bottom right;
  @media (prefers-reduced-motion: no-preference) {
    animation: ${rise} 200ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:focus { outline: none; }
  ::selection { background: ${p => p.theme.colors.successLight}; color: ${p => p.theme.colors.text}; }

  @media (max-width: ${p => p.theme.breakpoints.md}) {
    top: 0;
    left: 0;
    right: 0;
    bottom: ${BOTTOM_BAR};
    width: auto;
    height: auto;
    border: 0;
    border-bottom: 1px solid ${p => p.theme.colors.border};
    border-radius: 0;
    box-shadow: none;
    padding-top: env(safe-area-inset-top);
    transform-origin: bottom center;
  }
`;

const Head = styled.header`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 60px;
  padding: 10px 10px 10px 18px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  .titles { flex: 1; min-width: 0; }
  h2 {
    margin: 0;
    font-size: 15.5px;
    font-weight: 650;
    letter-spacing: -0.015em;
    line-height: 1.25;
  }
  p {
    margin: 2px 0 0;
    font-size: 12.5px;
    line-height: 1.3;
    color: ${p => p.theme.colors.textMuted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const IconBtn = styled.button`
  flex: none;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 19px;
  cursor: pointer;
  &:hover:not(:disabled) { background: ${p => p.theme.colors.surfaceHover}; color: ${p => p.theme.colors.text}; }
  &:disabled { color: ${p => p.theme.colors.offline}; cursor: default; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: -2px; }
  @media (max-width: ${p => p.theme.breakpoints.sm}) { width: 44px; height: 44px; }
  ${p => p.$desktopOnly && css`
    @media (max-width: ${p.theme.breakpoints.md}) { display: none; }
  `}
`;

const Body = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Scroller = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: ${p => p.theme.colors.borderHover} transparent;
  padding: 18px 18px 8px;
  @media (max-width: ${p => p.theme.breakpoints.md}) { padding: 16px 16px 8px; }
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  max-width: 68ch;
  margin: 0 auto;
  padding-bottom: 8px;
`;

const Question = styled.p`
  align-self: flex-end;
  max-width: 86%;
  margin: 0;
  padding: 9px 13px;
  border-radius: 16px 16px 4px 16px;
  background: ${p => p.theme.colors.surfaceHover};
  color: ${p => p.theme.colors.text};
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Empty = styled.div`
  max-width: 68ch;
  margin: 0 auto;
  padding: 6px 2px 4px;
  .lead {
    margin: 0 0 16px;
    font-size: 15px;
    line-height: 1.55;
    color: ${p => p.theme.colors.textSecondary};
    text-wrap: pretty;
  }
  .note {
    margin: 16px 0 0;
    font-size: 12.5px;
    line-height: 1.45;
    color: ${p => p.theme.colors.textMuted};
  }
`;

const Starters = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  button {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 46px;
    padding: 10px 14px;
    border: 1px solid ${p => p.theme.colors.border};
    border-radius: 12px;
    background: ${p => p.theme.colors.surface};
    color: ${p => p.theme.colors.text};
    font: inherit;
    font-size: 14.5px;
    line-height: 1.35;
    text-align: left;
    cursor: pointer;
    transition: background 120ms ease-out, border-color 120ms ease-out;
  }
  button svg { flex: none; font-size: 18px; color: ${p => p.theme.colors.textMuted}; }
  button:hover { background: ${p => p.theme.colors.surfaceHover}; border-color: ${p => p.theme.colors.borderHover}; }
  button:hover svg { color: ${p => p.theme.colors.textSecondary}; }
  button:active { transform: scale(0.99); }
  button:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 1px; }
`;

const Latest = styled.button`
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 999px;
  background: ${p => p.theme.colors.surface};
  color: ${p => p.theme.colors.textSecondary};
  box-shadow: ${p => p.theme.shadows.md};
  font: inherit;
  font-size: 13px;
  font-weight: 550;
  cursor: pointer;
  svg { font-size: 14px; }
  &:hover { color: ${p => p.theme.colors.text}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 2px; }
`;

const Composer = styled.form`
  flex: none;
  padding: 10px 14px 12px;
  border-top: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.surface};
  .field {
    max-width: 68ch;
    margin: 0 auto;
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 6px 6px 6px 14px;
    border: 1px solid ${p => p.theme.colors.borderHover};
    border-radius: 14px;
    background: ${p => p.theme.colors.background};
    transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
  }
  .field:focus-within {
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 1px ${p => p.theme.colors.primary};
  }
  textarea {
    flex: 1;
    min-width: 0;
    min-height: 24px;
    max-height: 168px;
    margin: 0 0 6px;
    padding: 2px 0 0;
    border: 0;
    outline: 0;
    resize: none;
    background: transparent;
    color: ${p => p.theme.colors.text};
    font: inherit;
    font-size: 15px;
    line-height: 1.5;
    caret-color: ${p => p.theme.colors.primary};
  }
  textarea::placeholder { color: ${p => p.theme.colors.textMuted}; opacity: 1; }
  .hint {
    max-width: 68ch;
    margin: 6px auto 0;
    font-size: 11.5px;
    color: ${p => p.theme.colors.textMuted};
    @media (pointer: coarse) { display: none; }
  }
`;

const SendBtn = styled.button`
  flex: none;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: ${p => p.theme.colors.primary};
  color: ${p => p.theme.colors.textInverse};
  font-size: 18px;
  cursor: pointer;
  transition: background 120ms ease-out, transform 120ms ease-out;
  &:hover:not(:disabled) { background: ${p => p.theme.colors.primaryHover}; }
  &:active:not(:disabled) { transform: scale(0.94); }
  &:disabled { background: ${p => p.theme.colors.surfaceHover}; color: ${p => p.theme.colors.textMuted}; cursor: default; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 2px; }
  ${p => p.$stop && css`
    background: ${p.theme.colors.text};
    color: ${p.theme.colors.surface};
    &:hover:not(:disabled) { background: ${p.theme.colors.textSecondary}; }
  `}
  @media (max-width: ${p => p.theme.breakpoints.sm}) { width: 44px; height: 44px; }
`;

/* Tagging an issue: the # button in the box, the list it opens, the chip it leaves. */
const TagBtn = styled.button`
  flex: none;
  width: 36px;
  height: 36px;
  margin-left: -8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: ${p => (p.$on ? p.theme.colors.surfaceHover : 'transparent')};
  color: ${p => (p.$on ? p.theme.colors.text : p.theme.colors.textMuted)};
  font-size: 18px;
  cursor: pointer;
  transition: background 120ms ease-out, color 120ms ease-out;
  &:hover:not(:disabled) { background: ${p => p.theme.colors.surfaceHover}; color: ${p => p.theme.colors.text}; }
  &:disabled { cursor: default; opacity: 0.5; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 2px; }
  @media (max-width: ${p => p.theme.breakpoints.sm}) { width: 44px; height: 44px; }
`;

const Picker = styled.div`
  max-width: 68ch;
  margin: 0 auto 8px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 12px;
  background: ${p => p.theme.colors.background};
  overflow: hidden;
  input {
    width: 100%;
    padding: 9px 12px;
    border: 0;
    border-bottom: 1px solid ${p => p.theme.colors.border};
    outline: 0;
    background: transparent;
    color: ${p => p.theme.colors.text};
    font: inherit;
    font-size: 14px;
  }
  input::placeholder { color: ${p => p.theme.colors.textMuted}; }
  ul { list-style: none; margin: 0; padding: 4px; max-height: 220px; overflow-y: auto; overscroll-behavior: contain; }
  li button {
    width: 100%;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px 8px;
    padding: 7px 8px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: ${p => p.theme.colors.text};
    font: inherit;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
  }
  li button:hover, li button:focus-visible { background: ${p => p.theme.colors.surfaceHover}; outline: 0; }
  .num { color: ${p => p.theme.colors.textMuted}; font-variant-numeric: tabular-nums; }
  .title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .meta { grid-column: 2; font-size: 12px; color: ${p => p.theme.colors.textMuted}; }
  .none { padding: 10px 12px; font-size: 13px; color: ${p => p.theme.colors.textMuted}; }
`;

const TagChip = styled.div`
  max-width: 68ch;
  margin: 0 auto 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px 6px 12px;
  border-radius: 10px;
  background: ${p => p.theme.colors.surfaceHover};
  font-size: 13px;
  color: ${p => p.theme.colors.textSecondary};
  b { color: ${p => p.theme.colors.text}; font-weight: 600; }
  .what { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  button {
    flex: none;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: ${p => p.theme.colors.textMuted};
    cursor: pointer;
  }
  button:hover { background: ${p => p.theme.colors.surface}; color: ${p => p.theme.colors.text}; }
`;

const QuestionTag = styled(Link)`
  display: block;
  margin-bottom: 2px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${p => p.theme.colors.textSecondary};
  text-decoration: none;
  &:hover { color: ${p => p.theme.colors.text}; text-decoration: underline; }
`;

const SrOnly = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

/* ------------------------------------------------------------------ component */

export default function ChatBubble() {
  const { user } = useAuth();
  const owner = user?.username || user?.id || 'me';
  const { messages, busy, send, stop, retry, reset } = useChat(owner);

  const [open, setOpen] = useState(() => readFlag(sessionStorage, OPEN_KEY));
  const [wide, setWide] = useState(() => readFlag(localStorage, WIDE_KEY));
  const [draft, setDraft] = useState('');
  const [viewer, setViewer] = useState(null); // {photos, index, title}
  const [pinned, setPinned] = useState(true); // following the bottom of the thread
  const [announce, setAnnounce] = useState('');
  const [tagged, setTagged] = useState(null);       // {id, title}: goes with the next question
  const [picking, setPicking] = useState(false);
  const [issueList, setIssueList] = useState(null); // null = not loaded yet
  const [issueQuery, setIssueQuery] = useState('');

  const scrollerRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);
  const panelRef = useRef(null);
  const pinnedRef = useRef(true);

  useEffect(() => writeFlag(sessionStorage, OPEN_KEY, open), [open]);
  useEffect(() => writeFlag(localStorage, WIDE_KEY, wide), [wide]);

  // Focus: into the box on open, back to the launcher on close.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open) {
      // On a phone, focusing the box would raise the keyboard over the starter
      // questions, so focus the panel itself; with a mouse, go straight to typing.
      const touch = window.matchMedia('(pointer: coarse)').matches;
      requestAnimationFrame(() => (touch ? panelRef : inputRef).current?.focus({ preventScroll: true }));
    } else if (wasOpen.current) {
      requestAnimationFrame(() => launcherRef.current?.focus({ preventScroll: true }));
    }
    wasOpen.current = open;
  }, [open]);

  // Follow the answer as it streams, unless the reader has scrolled up to read.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight < 64;
    pinnedRef.current = atEnd;
    setPinned(atEnd);
  };

  const toLatest = () => {
    const el = scrollerRef.current;
    if (!el) return;
    pinnedRef.current = true;
    setPinned(true);
    el.scrollTo({ top: el.scrollHeight, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  // One announcement per state change, never per streamed chunk: a live region that
  // re-reads a growing paragraph is unusable with a screen reader.
  const last = messages[messages.length - 1];
  const lastStatus = last?.role === 'assistant' ? last.status : null;
  const lastSteps = last?.role === 'assistant' ? last.steps.length : 0;
  useEffect(() => {
    if (!last || last.role !== 'assistant') return;
    if (lastStatus === 'streaming') {
      const step = last.steps[last.steps.length - 1];
      setAnnounce(step ? `Checking: ${step.label}` : 'Thinking');
    } else if (lastStatus === 'done') {
      setAnnounce(`Answer: ${plainText(last.content)}`);
    } else if (lastStatus === 'error') {
      setAnnounce(`Error: ${last.error}`);
    } else if (lastStatus === 'stopped') {
      setAnnounce('Stopped.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastStatus, lastSteps]);

  // Open issues first, then the closed ones; loaded when the list is first opened.
  useEffect(() => {
    if (!picking || issueList) return;
    issuesAPI.list({ status: 'all' })
      .then(({ data }) => {
        const all = data.issues || [];
        const active = s => s === 'open' || s === 'in_progress';
        setIssueList([...all.filter(i => active(i.status)), ...all.filter(i => !active(i.status))]);
      })
      .catch(() => setIssueList([]));
  }, [picking, issueList]);

  // Another page asked for an issue to be tagged ("Ask the farm" on an issue).
  useEffect(() => {
    const onTag = (e) => {
      const { id, title } = e.detail || {};
      if (id == null) return;
      setTagged({ id: Number(id), title: title || `Issue #${id}` });
      setPicking(false);
      setOpen(true);
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    };
    window.addEventListener(TAG_ISSUE_EVENT, onTag);
    return () => window.removeEventListener(TAG_ISSUE_EVENT, onTag);
  }, []);

  const shownIssues = (issueList || []).filter((i) => {
    const q = issueQuery.trim().toLowerCase().replace(/^#/, '');
    return !q || String(i.id) === q || (i.title || '').toLowerCase().includes(q);
  }).slice(0, 40);

  const pickIssue = (issue) => {
    setTagged({ id: issue.id, title: issue.title });
    setPicking(false);
    setIssueQuery('');
    requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
  };

  const grow = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, []);
  useLayoutEffect(grow, [draft, open, grow]);

  const ask = (text) => {
    if (busy) return;
    if (send(text, undefined, tagged || undefined)) {
      setDraft('');
      setTagged(null);
      setPicking(false);
      pinnedRef.current = true;
      setPinned(true);
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (busy) stop();
    else ask(draft);
  };

  const onKeyDown = (e) => {
    // On a touch keyboard Enter is a new line; the send button sends.
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    e.preventDefault();
    if (!busy) ask(draft);
  };

  const onPanelKey = (e) => {
    if (e.key === 'Escape' && picking) {
      e.stopPropagation();
      setPicking(false);
    } else if (e.key === 'Escape' && !viewer) {
      e.stopPropagation();
      setOpen(false);
    }
  };

  const newChat = () => {
    reset();
    setDraft('');
    setTagged(null);
    setPicking(false);
    pinnedRef.current = true;
    setPinned(true);
    inputRef.current?.focus();
  };

  const openPhotos = useCallback((images, index, plantId, label) => {
    setViewer({
      title: plantId ? `Plant ${plantId}` : label || 'Photo',
      index,
      photos: images.map(img => ({ id: img.full || img.thumb, url: img.full || img.thumb, thumb: img.thumb })),
    });
  }, []);

  const onViewerIndex = useCallback(i => setViewer(v => (v ? { ...v, index: i } : v)), []);
  const closeViewer = useCallback(() => setViewer(null), []);

  if (!open) {
    return (
      <Launcher ref={launcherRef} type="button" onClick={() => setOpen(true)} aria-label="Ask the farm assistant" aria-expanded="false">
        <PiChatCircleText aria-hidden="true" />
        <span className="label">Ask the farm</span>
      </Launcher>
    );
  }

  return (
    <>
      <Panel ref={panelRef} tabIndex={-1} $wide={wide} role="dialog" aria-modal="false" aria-labelledby="farm-assistant-title" onKeyDown={onPanelKey}>
        <Head>
          <div className="titles">
            <h2 id="farm-assistant-title">Farm assistant</h2>
            <p>Answers from the farm's records and photos</p>
          </div>
          <IconBtn type="button" onClick={newChat} disabled={!messages.length} aria-label="New chat" title="New chat">
            <PiNotePencil aria-hidden="true" />
          </IconBtn>
          <IconBtn type="button" $desktopOnly onClick={() => setWide(w => !w)}
            aria-label={wide ? 'Make the panel narrower' : 'Make the panel wider'} title={wide ? 'Narrower' : 'Wider'}>
            {wide ? <PiArrowsIn aria-hidden="true" /> : <PiArrowsOut aria-hidden="true" />}
          </IconBtn>
          <IconBtn type="button" onClick={() => setOpen(false)} aria-label="Close the assistant" title="Close (Esc)">
            <PiX aria-hidden="true" />
          </IconBtn>
        </Head>

        <Body>
          <Scroller ref={scrollerRef} onScroll={onScroll}>
            {!messages.length ? (
              <Empty>
                <p className="lead">
                  Ask about the tanks, the container's climate, power, the plants and their photos,
                  or today's work. It looks up the records before it answers.
                </p>
                <Starters aria-label="Questions to start with">
                  {STARTERS.map(({ icon: Icon, text }) => (
                    <li key={text}>
                      <button type="button" onClick={() => ask(text)}>
                        <Icon aria-hidden="true" />
                        <span>{text}</span>
                      </button>
                    </li>
                  ))}
                </Starters>
                <p className="note">It can still get things wrong. Check the page before you act on an answer.</p>
              </Empty>
            ) : (
              <Thread>
                {messages.map(m => (m.role === 'user'
                  ? (
                    <Question key={m.id}>
                      {m.issue && <QuestionTag to={`/issues/${m.issue.id}`}>#{m.issue.id} {m.issue.title}</QuestionTag>}
                      {m.content}
                    </Question>
                  )
                  : (
                    <AssistantTurn key={m.id} message={m} onRetry={retry} onOpenPhotos={openPhotos}
                      canRetry={!busy} />
                  )))}
              </Thread>
            )}
          </Scroller>
          {!pinned && messages.length > 0 && (
            <Latest type="button" onClick={toLatest}>
              <PiArrowDown aria-hidden="true" />
              Latest
            </Latest>
          )}
        </Body>

        <Composer onSubmit={onSubmit}>
          {picking && (
            <Picker role="dialog" aria-label="Tag an issue">
              <input
                autoFocus
                value={issueQuery}
                onChange={e => setIssueQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); if (shownIssues[0]) pickIssue(shownIssues[0]); }
                }}
                placeholder="Search issues by title or #number"
                aria-label="Search issues"
              />
              {issueList === null ? <div className="none">Loading issues</div>
                : !shownIssues.length ? <div className="none">{issueList.length ? 'No issue matches.' : 'No issues logged yet.'}</div>
                  : (
                    <ul>
                      {shownIssues.map(i => (
                        <li key={i.id}>
                          <button type="button" onClick={() => pickIssue(i)}>
                            <span className="num">#{i.id}</span>
                            <span className="title">{i.title}</span>
                            <span className="meta">
                              {(i.status || '').replace('_', ' ')}, {i.severity}
                              {i.photo_count ? `, ${i.photo_count} photo${i.photo_count === 1 ? '' : 's'}` : ''}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
            </Picker>
          )}
          {tagged && !picking && (
            <TagChip>
              <PiHash aria-hidden="true" />
              <span className="what" title="It reads the issue's trail and every photo on it before answering">
                <b>#{tagged.id} {tagged.title}</b>
              </span>
              <span>All photos</span>
              <button type="button" onClick={() => setTagged(null)} aria-label={`Remove issue #${tagged.id}`} title="Remove">
                <PiX aria-hidden="true" />
              </button>
            </TagChip>
          )}
          <div className="field">
            <TagBtn type="button" $on={picking} onClick={() => setPicking(v => !v)} disabled={busy}
              aria-expanded={picking} aria-label="Tag an issue" title="Tag an issue">
              <PiHash aria-hidden="true" />
            </TagBtn>
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              maxLength={4000}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={tagged ? `Ask about issue #${tagged.id}` : 'Ask about the farm'}
              aria-label="Your question for the farm assistant"
            />
            {busy ? (
              <SendBtn type="submit" $stop aria-label="Stop the answer" title="Stop">
                <PiStop aria-hidden="true" />
              </SendBtn>
            ) : (
              <SendBtn type="submit" disabled={!draft.trim()} aria-label="Send" title="Send">
                <PiArrowUp aria-hidden="true" />
              </SendBtn>
            )}
          </div>
          <div className="hint">Enter sends. Shift + Enter starts a new line.</div>
        </Composer>

        <SrOnly aria-live="polite" aria-atomic="true">{announce}</SrOnly>
      </Panel>

      {viewer && (
        <Lightbox photos={viewer.photos} index={viewer.index} title={viewer.title}
          onClose={closeViewer} onIndex={onViewerIndex} />
      )}
    </>
  );
}
